import ConfigurationService from '../Service/ConfigurationService';
import { GeoJsonFileListResponse, PaginatedResponse, RequestConfig, Tree, TreeValidation } from 'tree-wallet-client';
import { request, requestOrThrow, httpFailure, isTokenExpired, readResponseBody, sessionEndedBy } from '../Helper/Http';
import { isNativeApp } from '../Dependency/NativeAppMemory';
import DBService, { TABLE_TREES } from '../Service/DBService';
import { dataOrNull, floatOrNull, integerOrNull, stringOrNull, toObject } from '../Helper/DataHelper';
import * as TreeService from '../Service/TreeService';
import message from "antd/es/message";
import { getToken } from '../Service/AuthService';
import { invalidateTreesListVersion } from '../Service/MapTreesListService';
import { saveFileToDownloads } from '../Dependency/nativeFileStore';
import { KML_DOWNLOAD_FAILED } from '../Helper/BannerMessages';

export interface TreeFormData {
  typeId: number;
  userId: number;
  contractId: number;
  lat: number;
  lng: number;
  speed: number;
  accuracy: number;
  number: number;
  photo: any;
}

interface TreeCountResult {
  trees: number;
}

export async function getIndexes() {
  return await DBService.getAllKeys(TABLE_TREES);
}

export async function getTotalTreeCount() {
  // The shell counts the queue's trees in one pass over its table; the browser store cannot,
  // and then the rows are read one by one instead
  const counted = await DBService.countTrees(TABLE_TREES);
  if (counted !== null) {
    return counted;
  }

  const keys = await DBService.getAllKeys(TABLE_TREES);
  let total = 0;

  for (const key of keys) {
    // The photo of each row is left where it is, and the page gets a frame between two rows
    total += await DBService.getTreeNumber(TABLE_TREES, key);
    await new Promise((resolve) => setTimeout(resolve, 0));
  }

  return total;
}

export async function getGeoJsons(config: RequestConfig, isPublic?: boolean): Promise<GeoJsonFileListResponse> {
  const { params } = config;
  let url = `${ConfigurationService.urls().treeGeoJsonListUrl}`

  if ((!params || !params.contractId) && !isPublic) {
    throw new Error('missing parameter contractId');
  }

  if (!config.admin && !isPublic) {
    url += `/${params.contractId}`;
  }

  return await requestOrThrow(url, config);
}

export async function getHighlightedCountries(config: RequestConfig = {}): Promise<any> {
  const url = ConfigurationService.urls().highlightedCountriesUrl;
  // Skip authentication for public endpoints
  return await requestOrThrow<any>(url, { ...config, disableAuth: true });
}

export async function getPublicGeoJsons(config: RequestConfig): Promise<GeoJsonFileListResponse> {
  const url = `${ConfigurationService.urls().treePublicGeoJsonListUrl}`

  return await requestOrThrow<GeoJsonFileListResponse>(url, config);
}

export async function getPublicCountByCountry(config: RequestConfig): Promise<any> {
  const { params } = config;

  if (!params || !params.country_code) {
    throw new Error('missing parameter country_code');
  }

  const url = `${ConfigurationService.urls().treePublicGeoJsonFileUrl}&countryId=${params.country_code}`
  console.log(url, 'URL OF THE PUBLIC COUNT BY COUNTRY')
  return await requestOrThrow(url, { ...config, disableAuth: true });
}

export async function getPublicTree(config: RequestConfig): Promise<Tree> {
  const { params } = config;

  if (!params || !params.treeId) {
    throw new Error('missing parameter treeId');
  }

  const url = `${ConfigurationService.urls().treePublicUrl}/${params.treeId}`
  return await requestOrThrow<Tree>(url, config);
}

export async function getTree(config: RequestConfig): Promise<Tree> {
  const { params } = config;

  if (!params || !params.treeId) {
    throw new Error('missing parameter treeId');
  }

  const url = `${ConfigurationService.urls().treeUrl}/${params.treeId}`
  return await requestOrThrow<Tree>(url, config);
}

export async function getTreeList(config: RequestConfig): Promise<PaginatedResponse<Tree[]>> {
  const url = `${ConfigurationService.urls().treeListUrl}`;

  // The shell keeps the list in its own store: marking the call is what keeps the service worker from caching a
  // second copy of it (see `service-worker.js`, which cannot read the WebView's user agent)
  const queryParams = isNativeApp() ? { ...config.queryParams, tw: 'copy' } : config.queryParams;

  const response = await requestOrThrow<PaginatedResponse<Tree[]>>(url, {
    ...config,
    queryParams,
  });

  return response;
}

export async function createTree(formData: FormData, successCallback?: Function, errorCallback?: Function) {
  let requestError: any = null;

  await request(
    ConfigurationService.urls().treeCreateUrl,
    {
      httpConfig: {
        method: 'POST',
        body: formData,
        multipart: true,
      },
      successCallback: (response: any) => {
        try {
          if (formData.has('number')) {
            TreeService.incrementTrees(formData.get('number'));
          }
        } catch (error) {
          // the tree is stored server side, a counter update must never undo that
          console.error('could not update the local tree counter', { error });
        }
        successCallback && successCallback(response)
      },
      errorCallback: (error: any) => {
        requestError = error;
        errorCallback && errorCallback(error);
      }
    }
  );

  // request() reports failures through the callback and resolves with undefined.
  // The offline queue needs a real rejection, otherwise a tree that was never
  // stored would be dropped from the queue as if it had been synced.
  if (requestError) {
    throw requestError;
  }
}

export async function getTreeCount(config?: RequestConfig): Promise<TreeCountResult> {
  let url = ConfigurationService.urls().treeCountUrl;
  if (config && config.params && config.params.contractId) {
    url = url + '/' + config.params.contractId;
  }

  if (!navigator.onLine) {
    config && config.successCallback && config.successCallback(TreeService.getTreeCount());
    return { trees: TreeService.getTreeCount() };
  }

  return await requestOrThrow(
    url,
    {
      successCallback: (treeCount: any) => {
        if (treeCount && treeCount.trees) {
          TreeService.setTreeCount(treeCount.trees);
        }
        config && config.successCallback && config.successCallback(treeCount)
      },
      errorCallback: (error: any) => {
        console.error('error fetching tree count', error);
        config && config.errorCallback && config.errorCallback(error);
      }
    }
  );
}

export async function getTreesDataFromIdb(nbTrees?: number) {
  const data: any[] = [];
  await DBService.getCursor(TABLE_TREES, (cursor: any) => {
    const item = formatTreeData(cursor.value);
    if (!nbTrees || data.length < nbTrees) {
      data.push({ data: item, id: cursor.key });
    }
  });

  return data;
}

export async function deleteTree(tree: Tree, config?: RequestConfig) {
  const number = tree.numberPlanted;
  const req = await requestOrThrow(
    `${ConfigurationService.urls().treeDeleteUrl}/${tree.id}`,
    {
      ...config,
      httpConfig: { method: 'DELETE', ...config?.httpConfig },
      successCallback: (response: any) => {
        TreeService.decrementTrees(number);
        // The map's files still hold the old trees list version: next Tree Map visit has to ask for them again
        invalidateTreesListVersion();
        config?.successCallback?.(response);
      }
    }
  );
  return req;
}

export async function getOne(id: number, onError?: Function) {
  const data = await DBService.getValue(TABLE_TREES, id, onError);
  return formatTreeData(data);
}

export async function processTrees(callback: Function) {
  const data: any[] = [];
  await DBService.getCursor(TABLE_TREES, (cursor: any) => {
    const item = formatTreeData(cursor.value);
    callback(item)
  });

  return data;
}

export async function moveTrees(treeIds: number[], numberOfTrees: number, contractId: number) {
  const url = `${ConfigurationService.urls().treeMoveUrl}?to=${contractId}&nb=${numberOfTrees}`;
  return await requestOrThrow(url, {
    httpConfig: {
      method: 'POST',
      body: JSON.stringify({ treeIds })
    },
    // Both contracts the trees left and joined are drawn from files that are now out of date
    successCallback: () => invalidateTreesListVersion(),
  });
}

export async function getTreeValidationHistory(config: RequestConfig): Promise<PaginatedResponse<TreeValidation[]>> {
  const { params } = config;

  if (!params || !params.treeId) {
    throw new Error('missing parameter treeId');
  }

  const url = `${ConfigurationService.urls().treeValidationHistoryUrl}/${params.treeId}`
  return await requestOrThrow<PaginatedResponse<TreeValidation[]>>(url, config);
}

/**
 * `request()` reports a failure through its errorCallback and still resolves, so a
 * validation rejected by the API resolved as a success: the offline queue took the
 * tree for synced, deleted it and told the user it had been stored, while nothing had
 * been stored server side. The failure is turned into a real rejection here, the same
 * way `createTree` does it.
 */
export async function validateTree(treeId: string, config?: RequestConfig) {
  const url = `${ConfigurationService.urls().treeValidationUrl}/${treeId}/validation`
  return await requestOrThrow(url, {
    ...config,
    // The tree is drawn differently once validated: what the map holds of it is out of date
    successCallback: (response: any) => {
      invalidateTreesListVersion();
      config?.successCallback?.(response);
    },
  });
}

export function normalize(data: any) {
  const treeData = {
    id: integerOrNull(data.id),
    contractId: integerOrNull(data.contractId),
    description: stringOrNull(data.description),
    numberPlanted: integerOrNull(data.numberPlanted),
    lat: floatOrNull(data.lat),
    lng: floatOrNull(data.lng),
    altitude: floatOrNull(data.altitude),
    accuracy: floatOrNull(data.accuracy),
    altitude_accuracy: floatOrNull(data.altitude_accuracy),
    // orientation: floatOrNull(data.orientation),
    validationHash: stringOrNull(data.validationHash),
    plantedHash: stringOrNull(data.plantedHash),
    fruit_tree: integerOrNull(data.fruit_tree),
    user: dataOrNull(data.user),
    contract: dataOrNull(data.contract),
    treeType: dataOrNull(data.treeType),
    photos: dataOrNull(data.photos),
    treeValidations: dataOrNull(data.treeValidations),
    createdAt: stringOrNull(data.createdAt),
    updatedAt: stringOrNull(data.updatedAt),
  };

  treeData["photos"] = toObject(treeData.photos);
  treeData["treeType"] = toObject(treeData.treeType);
  treeData["user"] = toObject(treeData.user);

  if (Array.isArray(treeData.treeValidations)) {
    treeData.treeValidations = treeData.treeValidations.map((v: any) => ({
      ...v,
      accuracy: floatOrNull(v.accuracy || v.gps_accuracy),
      altitude: floatOrNull(v.altitude),
      altitude_accuracy: floatOrNull(v.altitude_accuracy || v.altitudeAccuracy),
    }));
  }

  return treeData;
}

function formatTreeData(data: any) {
  // If data is already an object (not FormData), return it as-is
  if (typeof data === 'object' && !(data instanceof FormData)) {
    return data;
  }
  // For FormData or other iterables, convert to object
  const item: any = Object.fromEntries(data);
  // We no longer create a Blob URL here because it leaks memory if not revoked.
  // The UI components should handle creating/revoking URLs for Blobs themselves.
  return item;
}

export interface KmlDownloadOptions {
  filter?: any;
  /** Called with the bytes received so far: the file is read as it arrives, so the overlay can show how far it is */
  onProgress?: (bytes: number) => void;
}

const KML_FILE_NAME = 'trees.kml';

export async function downloadKML(options: KmlDownloadOptions = {}): Promise<string | null> {
  const { filter, onProgress } = options;

  try {
    let url = `${ConfigurationService.urls().downloadkml}`;

    // Add filter parameter as JSON string if filter exists and has values
    if (filter && hasFilterValues(filter)) {
      url += `?filter=${encodeURIComponent(JSON.stringify(filter))}`;
    }

    const response = await fetch(url, {
      method: 'GET',
      headers: {
        'Authorization': `Bearer ${getToken()}`
      }
    });

    if (!response.ok) {
      // Classified like every other answer, so a rejected token ends the session here too
      const failure = httpFailure(response.status, response.statusText, await readResponseBody(response));

      console.error('KML download failed', { status: response.status, message: failure.message });
      sessionEndedBy(failure);
      throw failure;
    }

    // Read in chunks: the file is built by the API as one string, so the progress is the read itself
    const reader = response.body?.getReader();
    const chunks: BlobPart[] = [];
    let received = 0;

    if (reader) {
      for (; ;) {
        const { done, value } = await reader.read();
        if (done) {
          break;
        }

        if (value) {
          chunks.push(value);
          received += value.byteLength;
          onProgress?.(received);
        }
      }
    } else {
      // No streamable body: the whole file at once, reported as a single step
      const text = await response.text();
      chunks.push(text);
      received = text.length;
      onProgress?.(received);
    }

    const blob = new Blob(chunks, { type: 'application/vnd.google-earth.kml+xml' });

    // The app writes it into the device's Download folder: a blob URL would leave the file inside the page
    const savedAs = await saveFileToDownloads(KML_FILE_NAME, blob);
    if (savedAs) {
      return savedAs;
    }

    // A browser has no folder to write to: the anchor is what hands the file over
    const blobURL = window.URL.createObjectURL(blob);
    const link = document.createElement('a');
    link.href = blobURL;
    link.setAttribute('download', KML_FILE_NAME);
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    window.URL.revokeObjectURL(blobURL);

    return null;
  } catch (error) {
    console.error('Error downloading KML:', error);

    // A session that ended is not a failed download: the page is already leaving for the login
    if (!isTokenExpired(error)) {
      message.error(KML_DOWNLOAD_FAILED);
    }

    throw error;
  }
}

// What one tree costs in the file the API builds: 391 B measured on a 904-tree contract, rounded up
const AVERAGE_KML_BYTES_PER_TREE = 400;

/** What the KML download will weigh: the API builds the file on demand, so it is worked out from the row count */
export async function estimateKmlBytes(filter: any, admin: boolean): Promise<{ trees: number; bytes: number }> {
  const page = await getTreeList({
    queryParams: { filter: JSON.stringify(filter), pageSize: 1, page: 1 },
    admin,
  });

  const trees = Number(page?.pager?.totalRecords ?? 0);

  return { trees, bytes: trees * AVERAGE_KML_BYTES_PER_TREE };
}

// Helper function to check if filter has meaningful values
function hasFilterValues(filter: any): boolean {
  if (!filter) return false;

  // Check if any meaningful filter values exist
  return !!(
    filter.contractId ||
    filter.treeId ||
    filter.dateStart ||
    filter.dateEnd ||
    filter.sortField ||
    filter.sortOrder
  );
}
